/**
 * API de citas programadas.
 */
import { apiFetch } from "./client";

export type EstadoCita =
  | "PROGRAMADA"
  | "CONFIRMADA"
  | "EN_ATENCION"
  | "COMPLETADA"
  | "CANCELADA"
  | "NO_ASISTIO";

/** La consulta de la que nació una cita. */
export interface OrigenClinico {
  /** CONTROL (sesión de un tratamiento) o DERIVACION (a otra especialidad). */
  tipo: "CONTROL" | "DERIVACION";
  episodio_id: number | null;
  cita_id: number | null;
  fecha: string | null;
  especialidad: string | null;
  profesional: string | null;
  numero_sesion: number | null;
  total_sesiones: number | null;
}

export interface Cita {
  id: number;

  paciente_id: number;
  paciente_nombre: string;
  profesional_id: number;
  profesional_nombre: string;
  especialidad_id: number;
  especialidad_nombre: string;
  /** Qué se presta. null en citas anteriores al catálogo de servicios. */
  servicio_id: number | null;
  servicio_nombre: string | null;
  sede_id: number;
  sede_nombre: string;

  inicio: string;
  fin: string;
  estado: EstadoCita;
  motivo: string | null;
  monto: string | null;
  pago_estado: string | null;
  pagada: boolean;
  /** Quién cobró y cuándo. Null en citas sin pago registrado. */
  pago_registrado_por_nombre: string | null;
  /** Quién dio el pago por bueno. Con origen APP no es quien lo registró. */
  pago_validado_por_nombre?: string | null;
  pago_origen?: string | null;
  pago_fecha: string | null;

  triaje_id: number | null;
  /** Sesión de un plan de tratamiento: no pasa por triaje. */
  plan_id: number | null;
  numero_sesion: number | null;
  plan_sesiones_totales: number | null;
  /** Si ya le tomaron el triaje en esta atención. */
  tiene_triaje: boolean;
  /** Dónde está en el flujo. Lo calcula el servidor: no lo derives aquí. */
  estado_flujo: string;
  estado_flujo_label: string;
  /** MOSTRADOR o APP: quién creó la cita. */
  origen?: string;
  estado_flujo_paso: number;
  asignacion_id: number | null;
  /**
   * De qué consulta salió esta cita, cuando salió de alguna.
   *
   * Lo arma el servidor a partir del plan o de la derivación, que son dos
   * caminos distintos por dentro y uno solo para quien lee: "esto salió de
   * aquella visita".
   */
  origen_clinico: OrigenClinico | null;
  episodio_id: number | null;

  /**
   * Si esta cita debe pasar por triaje antes de la consulta.
   *
   * False cuando la especialidad no tria (Odontología no le toma la presión a
   * nadie) o cuando la cita viene de otra atención, donde ya se le tomaron las
   * vitales. `triaje_motivo` dice cuál de las dos, para poder escribirlo en
   * pantalla en vez de dejar el hueco.
   *
   * No requerido NO es prohibido: enfermería puede tomarlo igual si el médico
   * lo pide. Lo único que cambia es que deja de bloquear la consulta y de
   * salir en la bandeja de trabajo.
   */
  triaje_requerido: boolean;
  triaje_motivo: string | null;

  creado_por_user_id: number | null;
  /** Quién la registró en admisión. */
  creado_por_nombre: string | null;
  created_at: string;
}

export interface CitaCreate {
  /** Si se omite, se cobra la consulta de la especialidad. */
  servicio_id?: number | null;
  paciente_id: number;
  profesional_id: number;
  especialidad_id: number;
  sede_id: number;
  inicio: string;
  fin: string;
  motivo?: string | null;
  triaje_id?: number | null;
  asignacion_id?: number | null;
  monto?: string | number | null;
}

export interface CitaUpdate {
  inicio?: string;
  fin?: string;
  estado?: EstadoCita;
  motivo?: string | null;
}

export interface ListCitasParams {
  profesional_id?: number;
  paciente_id?: number;
  desde?: string;
  hasta?: string;
  fecha?: string;
  mias?: boolean;
  incluir_canceladas?: boolean;
  por_cobrar?: boolean;
  /** Busca la cita por el paciente: nombre, apellido o documento. */
  q?: string;
}

export function listCitas(params: ListCitasParams = {}): Promise<Cita[]> {
  const qs = new URLSearchParams();
  if (params.profesional_id) qs.set("profesional_id", String(params.profesional_id));
  if (params.paciente_id) qs.set("paciente_id", String(params.paciente_id));
  if (params.desde) qs.set("desde", params.desde);
  if (params.hasta) qs.set("hasta", params.hasta);
  if (params.fecha) qs.set("fecha", params.fecha);
  if (params.mias) qs.set("mias", "true");
  if (params.incluir_canceladas) qs.set("incluir_canceladas", "true");
  if (params.por_cobrar) qs.set("por_cobrar", "true");
  if (params.q) qs.set("q", params.q);
  const q = qs.toString();
  return apiFetch<Cita[]>(`/v1/citas${q ? `?${q}` : ""}`);
}

/** Inicia la atención de una cita. Falla (409) si la cita no está pagada. */
export function iniciarAtencion(citaId: number): Promise<Cita> {
  return apiFetch<Cita>(`/v1/citas/${citaId}/iniciar-atencion`, { method: "POST" });
}

export function createCita(payload: CitaCreate): Promise<Cita> {
  return apiFetch<Cita>("/v1/citas", {
    method: "POST",
    body: payload,
  });
}

export function getCita(id: number): Promise<Cita> {
  return apiFetch<Cita>(`/v1/citas/${id}`);
}

export function updateCita(id: number, payload: CitaUpdate): Promise<Cita> {
  return apiFetch<Cita>(`/v1/citas/${id}`, {
    method: "PATCH",
    body: payload,
  });
}

export function cancelarCita(id: number): Promise<Cita> {
  return updateCita(id, { estado: "CANCELADA" });
}

export function confirmarCita(id: number): Promise<Cita> {
  return updateCita(id, { estado: "CONFIRMADA" });
}
